import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Link2, Info, Zap, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import ChannelBehaviorEditor from '@/components/chatbot/ChannelBehaviorEditor';

export const TelegramChannel = ({
  chatbotId,
  chatbot,
  channels,
  setChannels,
  savingChannel,
  setSavingChannel,
  saveChannel,
  disconnectChannel,
  loadChannels,
}) => {
  const connected = !!channels.telegram?.connected;
  return (
    <div className="p-4 rounded-xl border border-slate-200 bg-gradient-to-r from-white to-sky-50/30">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#0088cc] flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">Telegram Bot</h4>
            <p className="text-sm text-slate-500">Auto-reply to Telegram messages</p>
          </div>
        </div>
        {connected && (
          <Badge className="bg-sky-100 text-sky-700">
            <CheckCircle className="w-3 h-3 mr-1" /> Connected
          </Badge>
        )}
      </div>

      {!connected ? (
        <div className="space-y-3">
          <div>
            <Label className="text-slate-600 text-sm">Bot Token</Label>
            <Input
              type="password"
              value={channels.telegram?.bot_token || ''}
              onChange={(e) => setChannels(prev => ({ ...prev, telegram: { ...prev.telegram, bot_token: e.target.value } }))}
              placeholder="From @BotFather on Telegram"
              className="bg-white border-slate-300 text-slate-900 mt-1"
              data-testid="telegram-token-input"
            />
          </div>
          <div className="flex items-center justify-between pt-2">
            <a href="https://core.telegram.org/bots#botfather" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              <Info className="w-4 h-4" /> How to create a bot
            </a>
            <Button
              onClick={() => saveChannel('telegram', channels.telegram)}
              disabled={savingChannel === 'telegram' || !channels.telegram?.bot_token}
              className="bg-[#0088cc] hover:bg-[#006699]"
              data-testid="connect-telegram-btn"
            >
              {savingChannel === 'telegram' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Connect Telegram
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-sky-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-sky-900">Connected to @{channels.telegram?.bot_username || 'Telegram Bot'}</p>
              <p className="text-xs text-sky-700">
                {channels.telegram?.webhook_active ? 'Webhook active - auto-replying to messages' : 'Connected - set up webhook to enable auto-reply'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => disconnectChannel('telegram')} className="text-red-600 border-red-200 hover:bg-red-50">
              Disconnect
            </Button>
          </div>
          {!channels.telegram?.webhook_active && (
            <Button
              data-testid="setup-telegram-webhook-btn"
              onClick={async () => {
                try {
                  setSavingChannel('telegram_webhook');
                  await api.post(`/webhooks/social/telegram/setup-webhook/${chatbot.chatbot_id}`);
                  toast.success('Telegram webhook configured! Your AI agent is now active.');
                  loadChannels();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to set up webhook');
                } finally {
                  setSavingChannel(null);
                }
              }}
              disabled={savingChannel === 'telegram_webhook'}
              className="w-full bg-[#0088cc] hover:bg-[#006699] text-white"
            >
              {savingChannel === 'telegram_webhook' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Activate AI Employee on Telegram
            </Button>
          )}
          {channels.telegram?.webhook_active && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 p-2 rounded-lg">
              <Zap className="w-4 h-4" />
              AI agent is actively responding to Telegram messages
            </div>
          )}
          <ChannelBehaviorEditor chatbotId={chatbotId} platform="telegram" label="Telegram" />
        </div>
      )}
    </div>
  );
};

export default TelegramChannel;
